import { useMemo, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { LocaleManager } from 'utils/locale/locale-manager';
import { Namespaces, SiteLanguageLabel, SiteLanguage } from 'types/locales';
import { LOCALE_TRACKING_MAP } from 'constants/locale-tracking';

import type { TenantId } from 'tenants/types/tenant';

/**
 * Centralized locale hook that provides all locale functionality.
 * Uses the LocaleManager for consistent behavior.
 */
export function useLocale(tenantId: TenantId) {
  const { t, ready } = useTranslation(Namespaces.COMMON);
  const router = useRouter();

  // Create locale manager
  const localeManager = useMemo(
    () => LocaleManager.forClient(tenantId, router.locale),
    [tenantId, router.locale]
  );

  // Get current state
  const currentLocale = localeManager.getCurrentLocale();
  const savedPreference = localeManager.getSavedPreference();
  const isEnabled = localeManager.isLocalesSwitchingEnabled();
  const availableLocales = localeManager.getAvailableLocales();

  // Change locale function
  const changeLocale = useCallback(
    async (locale: string) => {
      await localeManager.switchToLocale(locale, router.asPath);
    },
    [localeManager, router.asPath]
  );

  // Create locale options for UI
  const localeOptions = useMemo(() => {
    return availableLocales.map(code => {
      const localeCode = typeof code === 'string' ? code : code;
      const labelKey = SiteLanguageLabel[localeCode as keyof typeof SiteLanguageLabel];
      
      return {
        id: localeCode,
        label: labelKey ? t(labelKey) : localeCode.toUpperCase(),
        isLink: false,
        onClick: LOCALE_TRACKING_MAP[localeCode as SiteLanguage],
      };
    });
  }, [availableLocales, t]);

  return {
    // Current state
    currentLocale,
    savedPreference,
    isEnabled,
    isReady: ready,

    // Available options
    availableLocales,
    localeOptions,

    // Actions
    changeLocale,

    // Utilities
    localeManager,
  };
}
